import React, { useEffect, useState, useRef, memo } from 'react';
import OffersFilterPanel from './OffersFilterPanel.js';
import AddressDisplay, { AddressDisplayLong } from './AddressDisplay.js';
import { useOffers } from '../hooks/useBlockchainData.js';
import { useBlockchainState } from '../contexts/BlockchainStateContext.js';
import './GameMaker.css';
import './sidebars.css';




const OfferCardComponent = ({ offer, acceptOffer, killGame, playerAccount, ethersUtils, games }) => {

  const [isExpanded, setIsExpanded] = useState(false);
  const [transactionStatus, setTransactionStatus] = useState({ status: 'idle', message: '' });

  // Function to interpret game parameters for display
  const getGameParameterDisplay = (gameName, gameParams) => {
    if (!gameParams || gameParams.length === 0) {
      return null;
    }

    // Games that use "who starts" parameter - match MakeOfferForm logic
    const isTicTacToe = gameName === 'TicTacToe';
    const isMillGame = gameName.includes('Mill');
    const isRiskGame = gameName.startsWith('Risk');

    if (isTicTacToe || isMillGame || isRiskGame) {
      const firstParam = gameParams[0];
      switch (String(firstParam)) {
        case '0':
          return { type: 'whoStarts', value: 'Player 1 starts', headerValue: '', color: 'text-blue-600', icon: '1️⃣' };
        case '1':
          return { type: 'whoStarts', value: 'Player 2 starts', headerValue: '', color: 'text-red-600', icon: '2️⃣' };
        case '2':
          return { type: 'whoStarts', value: 'Random start', headerValue: '', color: 'text-green-600', icon: '🎲' };
        default:
          return { type: 'whoStarts', value: `Custom (${firstParam})`, headerValue: `(${firstParam})`, color: 'text-gray-600', icon: '⚙️' };
      }
    }

    // For other games, show generic parameter info
    if (gameParams.length === 1) {
      return { type: 'generic', value: `Param: ${gameParams[0]}`, headerValue: `(${gameParams[0]})`, color: 'text-gray-600', icon: '⚙️' };
    } else {
      return { type: 'generic', value: `Params: [${gameParams.join(', ')}]`, headerValue: `([${gameParams.join(', ')}])`, color: 'text-gray-600', icon: '⚙️' };
    }
  };

  const paramDisplay = getGameParameterDisplay(offer.gameName, offer.gameParams);

  // Find the game address from the games array using the game name
  const getGameAddress = () => {
    if (!games || !offer.gameName) return null;
    const game = games.find(g => g.name === offer.gameName);
    return game ? game.gameAddress : null;
  };

  const gameAddress = getGameAddress();

  const handleAcceptOffer = async () => {
    // Set transaction status to pending
    setTransactionStatus({ status: 'pending', message: 'Transaction pending' });
    setIsExpanded(false); // Collapse the offer details
    
    try {
      // Call acceptOffer and wait for completion
      await acceptOffer(offer.offerID, offer.minPlayer2Bet);
      
      // Set success status
      setTransactionStatus({ status: 'success', message: 'Offer accepted' });
      
      // Clear success message after 5 seconds
      setTimeout(() => {
        setTransactionStatus({ status: 'idle', message: '' });
      }, 5000);
      
    } catch (error) {
      console.error("Accept offer transaction failed:", error);
      setTransactionStatus({ status: 'error', message: 'Transaction failed - click to retry' });
    }
  };

  const isPlayer1 = offer.player1 && String(offer.player1).toLowerCase() === (playerAccount || '').toLowerCase();
  const isTargetedOffer = offer.targetPlayer && offer.targetPlayer !== '0x0000000000000000000000000000000000000000';
  const isTargetPlayer = isTargetedOffer && String(offer.targetPlayer).toLowerCase() === (playerAccount || '').toLowerCase();
  const canAccept = !isPlayer1 && (!isTargetedOffer || isTargetPlayer);
  const acceptButtonClass = canAccept ? '' : 'grey';
  const killButtonClass = isPlayer1 ? '' : 'grey';
  
  // Determine background color and styling based on transaction status
  const isPending = transactionStatus.status === 'pending';
  const isSuccess = transactionStatus.status === 'success';
  const isError = transactionStatus.status === 'error';
  const showTransactionStatus = transactionStatus.status !== 'idle';
  
  // Determine base color by offer relationship to user
  const getOfferBaseColor = () => {
    if (offer.accepted) return '#cccccc'; // Gray for accepted offers
    
    // Personal offer TO user (requiring attention)
    if (isTargetedOffer && isTargetPlayer) {
      return '#fef3c7'; // Moderate light orange
    }
    
    // Offer made BY user (affecting)
    if (isPlayer1) {
      return '#ecfdf5'; // Moderate light green
    }
    
    // General open offer (not involving user)
    return '#ffffff'; // White
  };

  const backgroundColor = isPending ? '#dbeafe' :
                         isSuccess ? '#dcfce7' :
                         isError ? '#fef2f2' :
                         getOfferBaseColor();

  const handleCardClick = (e) => {
    e.stopPropagation();

    console.log('🎯 OFFER DEBUG: Expansion clicked', {
      offerID: offer?.offerID?.toString(),
      currentExpanded: isExpanded,
      clickTimestamp: Date.now(),
      action: !isExpanded ? 'EXPANDING' : 'COLLAPSING'
    });

    // Allow retry on error state
    if (isError) {
      setTransactionStatus({ status: 'idle', message: '' });
      setIsExpanded(!isExpanded);
    } else if (!showTransactionStatus) {
      // Only allow expand/collapse when not showing transaction status
      setIsExpanded(!isExpanded);
    }

    console.log('🎯 OFFER DEBUG: setIsExpanded called', {
      offerID: offer?.offerID?.toString(),
      newExpanded: !isExpanded,
      setStateTimestamp: Date.now()
    });

    // Track React commit timing
    const commitTracker = Date.now();
    setTimeout(() => {
      console.log('⚡ PERF DEBUG: React commit completed', {
        offerID: offer?.offerID?.toString(),
        commitTime: Date.now() - commitTracker,
        action: 'REACT_COMMIT_COMPLETE'
      });
    }, 0);
  };


  return (
    <div
      className={`border rounded-lg shadow-md p-4 mb-4 bg-white transition ${
        isPending ? 'pulse-blue border-blue-300' :
        isSuccess ? 'border-green-300' :
        isError ? 'border-red-300' :
        ''
      } ${showTransactionStatus ? 'p-6' : 'p-4'}`}
      style={{ backgroundColor }}
    >
      {/* Clickable header area for expand/collapse */}
      <div 
        className="cursor-pointer hover:bg-gray-50 -m-4 p-4 rounded-t-lg transition"
        onClick={handleCardClick}
      >
        <div className="flex justify-between">
          <div className="flex items-center">
            <div className="text-lg font-bold">{offer.gameName}</div>
            {paramDisplay && (
              <span className={`ml-3 text-sm ${paramDisplay.color} font-medium`}>
                <span className="mr-1">{paramDisplay.icon}</span>
                {paramDisplay.headerValue}
              </span>
            )}
          </div>
          <div className="text-sm text-gray-500">Timeout: {Math.floor(Number(offer.timeoutSinceLastMove) / 60)} min</div>
        </div>
        <div className="flex justify-between mt-2">
          <div className="text-sm">
            {ethersUtils.fromWei(offer.player1Bet.toString(), 'ether')} offered + {ethersUtils.fromWei(offer.minPlayer2Bet.toString(), 'ether')} expected
            <span className="ml-2 text-gray-600">
              (ratio: {(parseFloat(ethersUtils.fromWei(offer.player1Bet.toString(), 'ether')) / parseFloat(ethersUtils.fromWei(offer.minPlayer2Bet.toString(), 'ether'))).toFixed(2)})
            </span>
          </div>
          <div className="text-sm">
            Status: {offer.accepted ? 'Accepted' : 'Open'}
            {offer.targetPlayer && offer.targetPlayer !== '0x0000000000000000000000000000000000000000' && (
              <span className="ml-2 text-blue-600 font-medium">🎯 Personal</span>
            )}
          </div>
        </div>
        
        {/* Transaction status or Expand/Collapse indicator */}
        <div className="text-center mt-2 text-sm">
          {showTransactionStatus ? (
            <div className={`font-medium ${
              isPending ? 'text-blue-600' :
              isSuccess ? 'text-green-600' :
              'text-red-600'
            }`}>
              {transactionStatus.message}
            </div>
          ) : (
            <div className="text-gray-500">
              {isExpanded ? '▲ Click to collapse' : '▼ Click to expand'}
            </div>
          )}
        </div>
      </div>

      {isExpanded && !showTransactionStatus && (
        <div className="mt-4 space-y-2">
          <div className="text-sm">Offer ID: {offer.offerID.toString()}</div>
          <div className="text-sm">Game Address: <AddressDisplayLong address={gameAddress} useExplorerLink={true} /></div>
          <div className="text-sm">Player 1: <AddressDisplayLong address={offer.player1} useExplorerLink={true} /> (Bet: {ethersUtils.fromWei(offer.player1Bet.toString(), 'ether')})</div>
          <div className="text-sm">Player 2: <AddressDisplayLong address={offer.player2} useExplorerLink={true} /> (Bet: {ethersUtils.fromWei(offer.minPlayer2Bet.toString(), 'ether')})</div>

          {/* Game Parameters Section */}
          {paramDisplay && (
            <div className="text-sm border-t pt-2">
              <div className="flex items-center">
                <span className="font-medium text-gray-700">Game Settings:</span>
                <span className={`ml-2 ${paramDisplay.color} font-medium`}>
                  <span className="mr-1">{paramDisplay.icon}</span>
                  {paramDisplay.value}
                </span>
              </div>
              {paramDisplay.type === 'whoStarts' && (
                <div className="mt-1 text-xs text-gray-500 ml-4">
                  {String(offer.gameParams[0]) === '0' && "The player who created this offer goes first"}
                  {String(offer.gameParams[0]) === '1' && "The player who accepts this offer goes first"}
                  {String(offer.gameParams[0]) === '2' && "Starting player will be randomly determined when game begins"}
                </div>
              )}
              {offer.gameParams && offer.gameParams.length > 0 && (
                <div className="mt-1 text-xs text-gray-400">
                  Raw parameters: [{offer.gameParams.map(param => String(param)).join(', ')}]
                </div>
              )}
            </div>
          )}

          {offer.targetPlayer && offer.targetPlayer !== '0x0000000000000000000000000000000000000000' && (
            <div className="text-sm border-t pt-2">
              <span className="font-medium text-blue-600">🎯 Personal to:</span> <AddressDisplayLong address={offer.targetPlayer} />
            </div>
          )}

          <div className="flex space-x-4">
            {!offer.accepted && (
              <>
                <button 
                  onClick={() => { 
                    if (canAccept) {
                      handleAcceptOffer();
                    }
                  }} 
                  className={`px-4 py-2 rounded-md ${canAccept ? 'bg-blue-500 text-white hover:bg-blue-600' : 'bg-gray-300 text-gray-500 cursor-not-allowed'}`}
                  disabled={!canAccept}
                  title={
                    isPlayer1 ? "You cannot accept your own offer" :
                    isTargetedOffer && !isTargetPlayer ? "This offer is personal to a specific player" :
                    "Accept this offer"
                  }
                >
                  Accept
                </button>
                {isPlayer1 && (
                  <button 
                    onClick={() => killGame(offer.offerID)} 
                    className={`bg-red-500 text-white px-4 py-2 rounded-md ${killButtonClass}`}
                  >
                    KillSwitch
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Custom comparison function for React.memo to prevent unnecessary re-renders
const areOfferPropsEqual = (prevProps, nextProps) => {
  // Silent memo comparison

  // Check if offer object itself changed (reference or content)
  if (prevProps.offer !== nextProps.offer) {
    // Deep check key offer properties that affect rendering
    const offerChanged =
      prevProps.offer.offerID !== nextProps.offer.offerID ||
      prevProps.offer.gameName !== nextProps.offer.gameName ||
      prevProps.offer.accepted !== nextProps.offer.accepted ||
      prevProps.offer.player1Bet !== nextProps.offer.player1Bet ||
      prevProps.offer.minPlayer2Bet !== nextProps.offer.minPlayer2Bet;

    if (offerChanged) {
      // Offer content changed
      return false; // Allow re-render
    }
  }

  // Check function reference stability (these should be stable with useCallback)
  if (prevProps.acceptOffer !== nextProps.acceptOffer ||
      prevProps.killGame !== nextProps.killGame) {
    // Function references changed
    return false; // Allow re-render
  }

  // Check other props
  if (prevProps.playerAccount !== nextProps.playerAccount ||
      prevProps.ethersUtils !== nextProps.ethersUtils ||
      prevProps.games !== nextProps.games) {
    // Other props changed
    return false; // Allow re-render
  }

  // Props are effectively equal - prevent re-render
  // Props unchanged - prevent re-render
  return true; // Prevent re-render
};

// Memoize OfferCard to prevent unnecessary re-renders
const OfferCard = memo(OfferCardComponent, areOfferPropsEqual);

const OffersTable = ({
  ethersUtils,
  acceptOffer,
  killGame,
  playerAccount,
  games,
  hideWrapper = false, // New prop to hide the collapsible wrapper
  preFilteredOffers = null // Pre-filtered offers from PersonalizedSection
}) => {
  // Silent table render

  const tableRef = useRef(null);

  // Get blockchain state
  const { isReady } = useBlockchainState();

  // Load filter preferences from localStorage with defaults (MUST be before hook calls)
  const [showOnlyMyOffers, setShowOnlyMyOffers] = useState(() => {
    const saved = localStorage.getItem('offersTable_showOnlyMyOffers');
    return saved !== null ? JSON.parse(saved) : false;
  });
  const [showOnlyPersonalOffers, setShowOnlyPersonalOffers] = useState(() => {
    const saved = localStorage.getItem('offersTable_showOnlyPersonalOffers');
    return saved !== null ? JSON.parse(saved) : false;
  });
  const [showOnlyActiveOffers, setShowOnlyActiveOffers] = useState(() => {
    const saved = localStorage.getItem('offersTable_showOnlyActiveOffers');
    return saved !== null ? JSON.parse(saved) : true;
  });
  const [selectedGame, setSelectedGame] = useState(() => {
    const saved = localStorage.getItem('offersTable_selectedGame');
    return saved !== null ? saved : 'All';
  });
  const [selectedGames, setSelectedGames] = useState(() => {
    const saved = localStorage.getItem('offersTable_selectedGames');
    return saved !== null ? JSON.parse(saved) : null; // null means "use default (all games)"
  });
  const [sortPriorities, setSortPriorities] = useState(() => {
    const saved = localStorage.getItem('offersTable_sortPriorities');
    return saved !== null ? JSON.parse(saved) : ['offerID'];
  });
  const [filterOfferID, setFilterOfferID] = useState(() => {
    const saved = localStorage.getItem('offersTable_filterOfferID');
    return saved !== null ? saved : '';
  });
  
  const [isCollapsed, setIsCollapsed] = useState(() => {
    const saved = localStorage.getItem('offersTable_isCollapsed');
    return saved !== null ? JSON.parse(saved) : false;
  });
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);

  // Get offers data using new hook (only when not using pre-filtered data)
  const shouldFetchOffers = !preFilteredOffers;
  const {
    data: offers,
    loading,
    error,
    refresh: refreshOffers
  } = useOffers({
    activeOnly: showOnlyActiveOffers,
    gameFilter: selectedGame === 'All' ? '' : selectedGame
  }, shouldFetchOffers);

  // Use pre-filtered offers if provided, otherwise use context data
  const queriedOffers = preFilteredOffers || offers || [];

  // Use centralized data - fetch offers when config is ready or filters change
  // Skip fetching if pre-filtered data is provided (PersonalizedSection use case)
  useEffect(() => {
    if (preFilteredOffers) {
      console.log('📦 OffersTable: Using pre-filtered data, skipping fetch');
      return;
    }

    if (!isReady) {
      console.log('⏳ OffersTable: Blockchain not ready, skipping fetch');
      return;
    }

    console.log('🔄 OffersTable: Data will be fetched automatically by useOffers hook');
    // Note: Data fetching is now handled automatically by the useOffers hook
  }, [preFilteredOffers, isReady, selectedGame, showOnlyActiveOffers]);

  // Save filter preferences to localStorage when they change
  useEffect(() => {
    localStorage.setItem('offersTable_showOnlyMyOffers', JSON.stringify(showOnlyMyOffers));
  }, [showOnlyMyOffers]);

  useEffect(() => {
    localStorage.setItem('offersTable_showOnlyPersonalOffers', JSON.stringify(showOnlyPersonalOffers));
  }, [showOnlyPersonalOffers]);

  useEffect(() => {
    localStorage.setItem('offersTable_showOnlyActiveOffers', JSON.stringify(showOnlyActiveOffers));
  }, [showOnlyActiveOffers]);

  useEffect(() => {
    localStorage.setItem('offersTable_selectedGame', selectedGame);
  }, [selectedGame]);

  useEffect(() => {
    localStorage.setItem('offersTable_selectedGames', JSON.stringify(selectedGames));
  }, [selectedGames]);

  // Set default to all games if no localStorage value exists and games are available
  useEffect(() => {
    if (selectedGames === null && games && games.length > 0) {
      setSelectedGames(games.map(g => g.name));
    }
  }, [games, selectedGames]);

  useEffect(() => {
    localStorage.setItem('offersTable_sortPriorities', JSON.stringify(sortPriorities));
  }, [sortPriorities]);

  useEffect(() => {
    localStorage.setItem('offersTable_filterOfferID', filterOfferID);
  }, [filterOfferID]);

  useEffect(() => {
    localStorage.setItem('offersTable_isCollapsed', JSON.stringify(isCollapsed));
  }, [isCollapsed]);


  // Filter offers first
  const filteredQueriedOffers = queriedOffers.filter((offer) => {
    const isPlayer1 = offer.player1 && String(offer.player1).toLowerCase() === (playerAccount || '').toLowerCase();
    const isSelectedGame = selectedGame === 'All' || String(offer.gameName).includes(selectedGame);
    const isPersonalOffer = offer.targetPlayer && 
                           offer.targetPlayer !== '0x0000000000000000000000000000000000000000' &&
                           String(offer.targetPlayer).toLowerCase() === (playerAccount || '').toLowerCase();
    
    // Handle multi-game selection
    let isSelectedInMultiGame = true;
    if (selectedGames === null) {
      isSelectedInMultiGame = true; // null means "all games" (default)
    } else if (selectedGames.length === 0) {
      isSelectedInMultiGame = false; // Empty array = show no games
    } else {
      isSelectedInMultiGame = selectedGames.includes(offer.gameName);
    }
    
    // Handle offer ID filtering
    const matchesOfferID = !filterOfferID || filterOfferID.trim() === '' || String(offer.offerID) === filterOfferID.trim();
    
    return (
      isSelectedGame &&
      isSelectedInMultiGame &&
      (!showOnlyMyOffers || isPlayer1) &&
      (!showOnlyPersonalOffers || isPersonalOffer) &&
      matchesOfferID
    );
  });

  // Sort filtered offers using priority list
  const sortedOffers = [...filteredQueriedOffers].sort((a, b) => {
    for (const sortBy of sortPriorities) {
      let comparison = 0;
      
      switch (sortBy) {
        case 'player2Bet':
          comparison = Number(b.minPlayer2Bet) - Number(a.minPlayer2Bet); // Descending
          break;
        case 'betRatio':
          const ratioA = Number(a.player1Bet) / Number(a.minPlayer2Bet);
          const ratioB = Number(b.player1Bet) / Number(b.minPlayer2Bet);
          comparison = ratioB - ratioA; // Descending (higher ratio first)
          break;
        case 'timeout':
          comparison = Number(a.timeoutSinceLastMove) - Number(b.timeoutSinceLastMove); // Ascending (shorter timeout first)
          break;
        case 'offerID':
        default:
          comparison = Number(b.offerID) - Number(a.offerID); // Descending (newest first)
          break;
      }
      
      if (comparison !== 0) {
        return comparison;
      }
    }
    return 0;
  });

  // Debug logging for offer display issues (can be removed when stable)
  // console.log("🔍 OFFERS DEBUG:", { queriedOffers: queriedOffers?.length || 0, offers: offers?.length || 0, preFilteredOffers: preFilteredOffers?.length || 0, filteredQueriedOffers: filteredQueriedOffers?.length || 0, sortedOffers: sortedOffers?.length || 0 });

  // Show loading or error state
  if (error) {
    return (
      <div className="mb-8 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
        <h3 className="text-lg font-semibold text-yellow-800 mb-2">Connection Error</h3>
        <p className="text-yellow-700">{error}</p>
      </div>
    );
  }

  if (!isReady || loading) {
    return (
      <div className="mb-8 p-4 bg-gray-50 border border-gray-200 rounded-lg">
        <h3 className="text-lg font-semibold text-gray-600 mb-2">Loading Offers...</h3>
        <p className="text-gray-500">{!isReady ? 'Initializing blockchain connection...' : 'Fetching latest offers...'}</p>
      </div>
    );
  }

  const activeOffers = sortedOffers.filter((offer) => !offer.accepted);
  const inactiveOffers = sortedOffers.filter((offer) => offer.accepted);

  const boxColor = activeOffers.length > 0 ? '#ccffcc' : '#cccccc';

  // Main content component
  const renderContent = () => (
    <div className="space-y-4">
      <OffersFilterPanel 
        games={games}
        selectedGame={selectedGame}
        setSelectedGame={setSelectedGame}
        selectedGames={selectedGames}
        setSelectedGames={setSelectedGames}
        showOnlyActive={showOnlyActiveOffers}
        setShowOnlyActive={setShowOnlyActiveOffers}
        showOnlyMyOffers={showOnlyMyOffers}
        setShowOnlyMyOffers={setShowOnlyMyOffers}
        showOnlyPersonalOffers={showOnlyPersonalOffers}
        setShowOnlyPersonalOffers={setShowOnlyPersonalOffers}
        filterOfferID={filterOfferID}
        setFilterOfferID={setFilterOfferID}
        sortPriorities={sortPriorities}
        setSortPriorities={setSortPriorities}
        isOpen={isFilterPanelOpen}
        togglePanel={() => setIsFilterPanelOpen(!isFilterPanelOpen)}
      />
      <div className="space-y-4">
        {sortedOffers.map((offer) => (
            <OfferCard
              key={offer.offerID.toString()}
              offer={offer}
              acceptOffer={acceptOffer}
              killGame={killGame}
              playerAccount={playerAccount}
              ethersUtils={ethersUtils}
              games={games}
            />
        ))}
      </div>
    </div>
  );

  // Return with or without wrapper based on hideWrapper prop
  if (hideWrapper) {
    return (
      <div className="mb-8" ref={tableRef}>
        {renderContent()}
      </div>
    );
  }

  return (
    <div className="mb-8" ref={tableRef}>
      <div className="flex-grow space-y-4">
        <div
          className={`collapsible-box ${isCollapsed ? 'collapsed' : 'expanded'}`} 
          style={{ backgroundColor: boxColor }}
        >
          <div className="collapsible-header" onClick={() => setIsCollapsed(!isCollapsed)}>
            <div>
              {showOnlyActiveOffers
                ? `${activeOffers.length} active offers`
                : `${activeOffers.length} active offers and ${inactiveOffers.length} inactive offers`}
            </div>
            <div className="text-center mt-2 text-gray-500 text-sm">
              {isCollapsed ? '▼ Click to expand' : '▲ Click to collapse'}
            </div>
          </div>

          {!isCollapsed && (
            <div className="collapsible-content">
              {renderContent()}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OffersTable;
